// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { totalsByYear, filterRows, sumBy } from '../data';
import { FATES } from '../domain';
import { stackedAreaSvg, lineChartSvg, legendHtml, type AreaBand } from '../components/charts';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatTonnes, formatPercent, formatMt, fyShort } from '../utils/format';

type Mode = 'fate' | 'recovery' | 'percapita';

export function renderTrends(root: HTMLElement, ctx: ViewContext): void {
  const d = ctx.data;
  let mode: Mode = 'fate';
  let percent = false;
  const series = totalsByYear(d);
  const xLabels = d.years.map(fyShort);

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Waste over time</h1>
      <p class="view-sub">${d.years.length} reporting years, ${escapeHtml(fyShort(d.firstYear))} to ${escapeHtml(fyShort(d.latestYear))}. Total waste keeps
        rising with the economy, but the share ${glossaryTerm('recovery-rate', 'recovered')} is rising faster — though not fast enough to hit the 2030 target.</p>
    </div>
    <div class="controls">
      <div class="seg" role="group" data-role="mode">
        <button class="seg-btn" data-mode="fate" aria-pressed="true">Waste by fate</button>
        <button class="seg-btn" data-mode="recovery" aria-pressed="false">Recovery rate</button>
        <button class="seg-btn" data-mode="percapita" aria-pressed="false">Per person</button>
      </div>
      <div class="seg" role="group" data-role="scale" hidden>
        <button class="seg-btn" data-scale="abs" aria-pressed="true">Tonnes</button>
        <button class="seg-btn" data-scale="pct" aria-pressed="false">Share</button>
      </div>
    </div>
    <div class="panel">
      <div class="panel-title" data-role="title"></div>
      <div class="panel-sub" data-role="sub"></div>
      <div data-role="chart"></div>
      <div data-role="legend"></div>
    </div>
    <div class="panel">
      <div class="panel-title">Then vs now</div>
      <div class="panel-sub">National totals, ${escapeHtml(fyShort(d.firstYear))} compared with ${escapeHtml(fyShort(d.latestYear))}.</div>
      <div class="table-scroll">
        <table class="data"><thead><tr><th>Measure</th><th class="t-num">${escapeHtml(fyShort(d.firstYear))}</th><th class="t-num">${escapeHtml(fyShort(d.latestYear))}</th><th class="t-num">Change</th></tr></thead>
        <tbody data-role="tbody"></tbody></table>
      </div>
    </div>
  `;

  const chartEl = root.querySelector('[data-role="chart"]') as HTMLElement;
  const legendEl = root.querySelector('[data-role="legend"]') as HTMLElement;
  const titleEl = root.querySelector('[data-role="title"]') as HTMLElement;
  const subEl = root.querySelector('[data-role="sub"]') as HTMLElement;
  const scaleSeg = root.querySelector('[data-role="scale"]') as HTMLElement;

  function fateBands(): AreaBand[] {
    return FATES.map((f) => ({
      key: f.key, label: f.label, colour: f.colour,
      values: d.years.map((yr) => sumBy(filterRows(d.rows, { yr }), (r) => r.f).get(f.key) || 0),
    }));
  }

  function build(): void {
    scaleSeg.hidden = mode !== 'fate';
    if (mode === 'fate') {
      titleEl.textContent = percent ? 'Composition of waste by fate' : 'Total waste by fate';
      subEl.textContent = percent ? 'Each year as 100%. Recycling (green) has grown its share as landfill (red) shrinks.' : 'Millions of tonnes a year, stacked by where the waste ends up.';
      chartEl.innerHTML = stackedAreaSvg(fateBands(), xLabels, { width: 760, height: 340, percent, yFmt: (v) => `${formatMt(v)}Mt` });
      legendEl.innerHTML = legendHtml(FATES.map((f) => ({ colour: f.colour, label: f.label })));
    } else if (mode === 'recovery') {
      titleEl.textContent = 'Resource recovery rate vs the 2030 target';
      subEl.textContent = `The recovery rate climbed from ${formatPercent(series[0].rate, 0)} to ${formatPercent(series[series.length - 1].rate, 0)}, but the National Waste Policy target is ${d.target.recoveryRate}% by 2030.`;
      chartEl.innerHTML = lineChartSvg(
        [
          { label: 'Recovery rate', colour: 'var(--accent-primary)', points: series.map((s, i) => ({ x: i, y: s.rate })), width: 2.6 },
          { label: '2030 target', colour: 'var(--status-warn)', points: series.map((_, i) => ({ x: i, y: d.target.recoveryRate })), dashed: true, width: 1.5 },
        ],
        { width: 760, height: 320, xLabels, yMax: 100, yFmt: (v) => `${v.toFixed(0)}%`, markers: true },
      );
      legendEl.innerHTML = legendHtml([{ colour: 'var(--accent-primary)', label: 'Recovery rate' }, { colour: 'var(--status-warn)', label: `${d.target.recoveryRate}% target` }]);
    } else {
      titleEl.textContent = 'Waste generated per person';
      subEl.textContent = 'Tonnes per resident each year — controlling for population growth.';
      chartEl.innerHTML = lineChartSvg(
        [{ label: 'Per person', colour: 'var(--accent-secondary)', points: series.map((s, i) => ({ x: i, y: s.perCapita ?? null })), width: 2.6 }],
        { width: 760, height: 320, xLabels, yFmt: (v) => `${v.toFixed(1)}t`, markers: true },
      );
      legendEl.innerHTML = legendHtml([{ colour: 'var(--accent-secondary)', label: 'Tonnes per person' }]);
    }
    root.querySelectorAll('[data-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-mode') === mode)));
  }

  // then vs now table
  const first = series[0], last = series[series.length - 1];
  const chg = (a: number, b: number) => { const p = ((b - a) / a) * 100; return `<span style="color:${p >= 0 ? 'var(--status-warn)' : 'var(--status-good)'}">${p >= 0 ? '+' : ''}${p.toFixed(0)}%</span>`; };
  const rows = [
    ['Total waste', formatTonnes(first.total), formatTonnes(last.total), chg(first.total, last.total)],
    ['Recovery rate', formatPercent(first.rate, 0), formatPercent(last.rate, 0), `<span style="color:var(--status-good)">+${(last.rate - first.rate).toFixed(0)} pts</span>`],
    ['Per person', first.perCapita != null ? `${first.perCapita.toFixed(2)} t` : '—', last.perCapita != null ? `${last.perCapita.toFixed(2)} t` : '—', first.perCapita && last.perCapita ? chg(first.perCapita, last.perCapita) : '—'],
  ];
  (root.querySelector('[data-role="tbody"]') as HTMLElement).innerHTML = rows
    .map((r) => `<tr style="cursor:default"><td>${r[0]}</td><td class="t-num">${r[1]}</td><td class="t-num">${r[2]}</td><td class="t-num">${r[3]}</td></tr>`)
    .join('');

  root.querySelector('[data-role="mode"]')?.addEventListener('click', (e) => { const b = (e.target as Element).closest('[data-mode]'); if (!b) return; mode = b.getAttribute('data-mode') as Mode; build(); });
  root.querySelector('[data-role="scale"]')?.addEventListener('click', (e) => {
    const b = (e.target as Element).closest('[data-scale]'); if (!b) return;
    percent = b.getAttribute('data-scale') === 'pct';
    scaleSeg.querySelectorAll('[data-scale]').forEach((x) => x.setAttribute('aria-pressed', String((x.getAttribute('data-scale') === 'pct') === percent)));
    build();
  });

  build();
}
